import React, { useState, useEffect } from 'react';
import OrganizerVerifiedBadge from '../components/OrganizerVerifiedBadge';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Plus, Search } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useListCache } from '../hooks/useListCache';
import {
  PublicOrganizer, normalizeBundesland, organizerAvatarColor, organizerInitials,
} from '@radevents/shared';
import BundeslandOptions from '../components/BundeslandOptions';
import PublicHeader from '../components/Layout/PublicHeader';
import api from '../services/api';
import { trackOrganizerFunnel } from '../services/trackingService';

export default function OrganizerList() {
  usePageTitle('Veranstalter');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const offer = searchParams.get('angebot');
  const offerQuery = offer === 'premium' || offer === 'promotion' || offer === 'free'
    ? `?angebot=${offer}` : '';
  const intentCopy = offer === 'promotion'
    ? {
        title: 'Wähle deinen Veranstalter',
        text: 'Danach wählst du das Event aus, das du hervorheben möchtest.',
      }
    : offer === 'premium'
      ? {
          title: 'Wähle deinen Veranstalter',
          text: 'Danach kannst du Premium für alle zugehörigen Events aktivieren.',
        }
      : offer === 'free'
        ? {
            title: 'Welchen Veranstalter möchtest du verwalten?',
            text: 'Wähle einen bestehenden Eintrag oder trage deinen Veranstalter neu ein.',
          }
        : { title: 'Veranstalter', text: undefined };
  const { cache, setCache } = useListCache<{ organizers: PublicOrganizer[], bundesland: string }>('organizers');
  const [organizers, setOrganizers] = useState<PublicOrganizer[]>(cache?.organizers ?? []);
  const [loading, setLoading] = useState(!cache);
  useScrollRestoration(!loading);
  const [search, setSearch] = useState('');
  const [bundesland, setBundesland] = useState(cache?.bundesland ?? '');

  useEffect(() => {
    if (cache) return; // already hydrated
    api.get('/organizers')
      .then(r => {
        setOrganizers(r.data);
        setCache({ organizers: r.data, bundesland: '' });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Über normalizeBundesland vergleichen: Altbestände können noch die Umlaut-Schreibweise
  // tragen, die Select-Werte sind immer kanonisch (ASCII "ue").
  const filtered = organizers.filter(o =>
    (!search || o.name.toLowerCase().includes(search.toLowerCase())) &&
    (!bundesland || normalizeBundesland(o.bundesland) === bundesland)
  );

  const openDetail = (o: PublicOrganizer) => {
    if (offer === 'premium' || offer === 'promotion' || offer === 'free') {
      trackOrganizerFunnel('organizer_selected', { offer, organizerId: o.id });
    }
    navigate(`/veranstalter/${o.id}${offerQuery}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>Radsport-Veranstalter in Deutschland | Radsport Events</title>
        <meta name="description" content="Alle Radsport-Veranstalter in Deutschland – RTF, Rennrad, Gravel und MTB. Finde Events nach Veranstalter und Bundesland." />
        <link rel="canonical" href={`${window.location.origin}/veranstalter`} />
        <meta property="og:title" content="Radsport-Veranstalter in Deutschland | Radsport Events" />
        <meta property="og:description" content="Alle Radsport-Veranstalter in Deutschland – RTF, Rennrad, Gravel und MTB." />
      </Helmet>
      <PublicHeader mobileActions={
        <Link to={`/veranstalter/eintragen${offerQuery}`} className="header-cta"
          style={{ display: 'inline-flex', alignItems: 'center', background: '#2C2C2C', color: '#fff', borderRadius: 999, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
          + Eintragen
        </Link>
      } />

      <div className="org-list-page">
        {/* Der Weg für alle, die hier nicht stehen — Vereine wie kommerzielle Anbieter. Ohne
            ihn endete die Suche in einer Sackgasse, und genau die, die von sich aus kommen,
            fanden keinen Knopf. */}
        {/* Titel und „Eintragen“ in einer Zeile, darunter „Du bist Veranstalter?“ als Textlink statt als
            eigener Kasten (28.10.2026): auf dem Handy standen Titel, Knopf, Hinweis, Suche und Auswahl
            untereinander und nahmen die halbe Seite, bevor der erste Verein kam. */}
        <div className="org-list-kopf">
          <h1 className="org-list-title">
            {intentCopy.title}
          </h1>
          {/* Am Handy steht „+ Eintragen“ oben im Kopf, wie auf der Startseite für Events (mobileActions). */}
          <Link to={`/veranstalter/eintragen${offerQuery}`} className="org-list-eintragen hide-mobile">
            <Plus size={16} aria-hidden />Veranstalter eintragen
          </Link>
        </div>
        {intentCopy.text ? (
          <p style={{ fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 15, lineHeight: 1.5, color: '#6A6A6A', margin: '7px 0 0' }}>
            {intentCopy.text}
          </p>
        ) : (
          // „Für Veranstalter“ hat keinen eigenen Menüpunkt mehr: wer hier sucht, ist oft selbst einer.
          <Link to="/fuer-veranstalter" className="org-list-angebot">
            Du bist Veranstalter? Pflege deine Events selbst<ChevronRight size={16} aria-hidden />
          </Link>
        )}

        <div className="org-list-filter">
          <label className="ov-suche">
            <Search size={17} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name suchen"
              aria-label="Veranstalter suchen"
            />
          </label>
          <select
            value={bundesland}
            onChange={e => { setBundesland(e.target.value); setCache({ organizers, bundesland: e.target.value }); }}
            className="org-list-land ov-land"
            aria-label="Region"
          >
            <option value="">Überall</option>
            <BundeslandOptions />
          </select>
        </div>

        <div className="ov-anzahl">
          {filtered.length === 1 ? '1 Veranstalter' : `${filtered.length} Veranstalter`}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6A6A6A' }}>Laden…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 50, textAlign: 'center', background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, color: '#6A6A6A' }}>
            <div>Keine Veranstalter gefunden.</div>
            <div style={{ marginTop: 10, fontSize: 14 }}>
              Dein Verein oder dein Unternehmen fehlt?{' '}
              <Link to={`/veranstalter/eintragen${offerQuery}`} style={{ color: '#922C57', fontWeight: 700 }}>Veranstalter eintragen</Link>
            </div>
          </div>
        ) : (
          /* Zeilen statt Kärtchen (30.10.2026): am Handy eine zusammenhängende Liste wie in den
             Einstellungen von iOS, am Rechner zwei Spalten. Je Zeile Ort und kommende Events, damit
             man sieht, wo etwas los ist, bevor man tippt. Ein echter Link statt `div` mit `onClick`,
             sonst geht kein neuer Tab und keine Tastatur. */
          <ul className="ov-liste">
            {filtered.map(o => {
              const count = o.upcomingEventCount;
              return (
                <li key={o.id}>
                  <Link to={`/veranstalter/${o.id}${offerQuery}`} className="ov-zeile"
                    onClick={e => { if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) { e.preventDefault(); openDetail(o); } }}>
                    {o.logoUrl ? (
                      <img className="ov-avatar ov-avatar--logo" src={o.logoUrl} alt="" loading="lazy" />
                    ) : (
                      <span className="ov-avatar" style={{ background: organizerAvatarColor(o.name) }} aria-hidden>
                        {organizerInitials(o.name)}
                      </span>
                    )}
                    <span className="ov-text">
                      <span className="ov-name">
                        <span className="ov-name-text">{o.name}</span>
                        <OrganizerVerifiedBadge verified={o.verified} compact />
                      </span>
                      <span className="ov-meta">
                        {o.city && <span>{o.city}</span>}
                        {count != null && count > 0 && (
                          <span className="ov-events">{count === 1 ? '1 kommendes Event' : `${count} kommende Events`}</span>
                        )}
                      </span>
                    </span>
                    <ChevronRight size={18} className="ov-pfeil" aria-hidden />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

    </div>
  );
}
